/* eslint-disable unicorn/filename-case -- legacy Upload*.tsx naming */

import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { Box, LinearProgress, Typography } from "@mui/material";
import { useDropzone } from "react-dropzone";
import type { FieldPath, FieldValues, PathValue } from "react-hook-form";
import { useController, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import useUploadFile from "@/hooks/use-upload-file";

import type { UploadMetadata } from "./types";

export type UploadDropzoneProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> = {
  meta: UploadMetadata;
  name: TName;
  cb?: (files: PathValue<TFieldValues, TName>) => void;
};

export function UploadDropzone<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({ meta, name, cb }: UploadDropzoneProps<TFieldValues, TName>) {
  const { control } = useFormContext<TFieldValues>();
  const { field } = useController<TFieldValues, TName>({ control, name });
  const { t } = useTranslation();

  const { onDrop, loading, uploadProgress } = useUploadFile({
    maxFileSize: 10,
    maxFiles: 1,
    uploadedFiles: [],
    metadata: meta,
    onFilesUploaded: (uploadedFiles) => {
      const current = field.value;
      const oldFiles = Array.isArray(current) ? [...current] : [];
      const newFiles = [...oldFiles, ...uploadedFiles] as PathValue<TFieldValues, TName>;
      void Promise.resolve().then(() => {
        field.onChange(newFiles);
      });
      cb?.(newFiles);
    },
  });

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (...args) => {
      void onDrop(...args);
    },
    disabled: field.disabled,
    onDropAccepted() {
      field.onBlur();
    },
  });

  const isLoading = loading === true;

  return (
    <Box
      {...getRootProps()}
      sx={{
        border: "2px dashed",
        borderColor: isDragActive ? "primary.main" : "grey.400",
        borderRadius: 1,
        p: 4,
        textAlign: "center",
        cursor: "pointer",
      }}
    >
      <input {...getInputProps()} />

      <CloudUploadIcon color="primary" fontSize="large" />
      {isLoading ? (
        <Box sx={{ width: "100%", p: 2 }}>
          <Typography align="center" gutterBottom>
            {t("common.uploading")} ({uploadProgress}
            %)
          </Typography>
          <LinearProgress value={uploadProgress} variant="determinate" />
        </Box>
      ) : (
        <>
          <Typography variant="h6">Déposez les fichiers ici</Typography>
          <Typography align="center" color={isLoading ? "text.disabled" : "text.primary"}>
            {t("delivery.form.docs-upload-note")}
          </Typography>
        </>
      )}
    </Box>
  );
}
